import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ApiLaw from '@api/ApiLaw';

export type Role = 'user' | 'assistant' | string;

export interface Message {
  _id: string;
  content: string;
  updatedAt: string;
  conversationId: string;
  role: Role;
  __v?: number;
  avatar?: string;
}


const QK = {
  conversationId: ['ai', 'conversationId'] as const,
  messages: (conversationId: string | null) => ['ai', 'messages', conversationId] as const,
};

export function useConversationId() {
  return useQuery<string | null>({
    queryKey: QK.conversationId,
    queryFn: async () => {
      const stored = localStorage.getItem('conversationId');
      if (stored) return stored;

      const path = '/api/conversations';
      const { response } = await ApiLaw.POST(path, {});
      const id = response?.data?._id ?? null;
      if (id) localStorage.setItem('conversationId', id);
      return id;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useClearConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      localStorage.removeItem('conversationId');
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: QK.conversationId });
      qc.removeQueries({ queryKey: QK.messages(null) });
    },
  });
}

export function useInfiniteMessages(conversationId: string | null, limit = 20) {
  return useInfiniteQuery<Message[]>({
    queryKey: QK.messages(conversationId),
    enabled: !!conversationId,
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      if (!conversationId) return [] as Message[];
      const params: Record<string, string | number> = { limit };
      if (pageParam) params.beforeCursor = String(pageParam);
      const path = `/api/conversations/${conversationId}/messages`;
      const { response } = await ApiLaw.GET(path, params);
      const raw = (response?.data ?? response?.data?.data) as unknown;
      return (Array.isArray(raw) ? raw : []) as Message[];
    },
    getNextPageParam: (lastPage) => {
      const list = lastPage ?? [];
      if (!list.length || list.length < limit) return undefined;
      return list[list.length - 1]?._id;
    },
  });
}

export function useSendMessage(conversationId: string | null) {
  return useMutation<{ response?: { data?: unknown } } | { data?: unknown }, Error, string>({
    mutationFn: async (content: string) => {
      if (!conversationId) throw new Error('Missing conversationId');
      const path = `/api/conversations/${conversationId}/messages`;
      return ApiLaw.POST(path, { content });
    },
  });
}


